<template>
  <div class="ds-input-field">
    <label :for="inputId" class="ds-input-field__label">{{ label }}</label>

    <div class="ds-input-field__box" :class="{ 'is-error': hasError }">
      <img
        v-if="iconLeft"
        :src="iconLeft"
        alt=""
        class="ds-input-field__icon"
        aria-hidden="true"
      >

      <input
        :id="inputId"
        ref="inputRef"
        v-bind="$attrs"
        :type="type"
        :value="modelValue"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="error ? messageId : undefined"
        class="ds-input-field__input"
        @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      >

      <!-- Acción a la derecha (p. ej. mostrar/ocultar contraseña). -->
      <slot name="suffix" />
    </div>

    <p v-if="error" :id="messageId" class="ds-input-field__message">
      <img :src="errorIcon" alt="" class="ds-input-field__message-icon" aria-hidden="true">
      {{ error }}
    </p>
  </div>
</template>

<!-- Los atributos extra (required, pattern...) van al <input>, no a la raíz. -->
<script lang="ts">
export default { inheritAttrs: false }
</script>

<script setup lang="ts">
import { computed, ref } from 'vue'
import * as utilidades from '@/utilidades'
import errorIcon from '../../../assets/ds/ic_error.svg'

/**
 * Campo de formulario con label, ícono opcional y estado de error.
 *
 * `error` es el mensaje a mostrar. `invalid` marca el campo en rojo sin
 * mensaje propio, para errores que se describen en otro campo (p. ej.
 * credenciales incorrectas, donde el texto va bajo la contraseña).
 */
const props = withDefaults(defineProps<{
  label: string
  modelValue?: string
  type?: string
  iconLeft?: string
  error?: string
  invalid?: boolean
}>(), {
  modelValue: '',
  type: 'text',
  iconLeft: undefined,
  error: undefined,
  invalid: false
})

const hasError = computed(() => props.invalid || !!props.error)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const inputRef = ref<HTMLInputElement | null>(null)
const uid = utilidades.generarId()
const inputId = computed(() => `ds-input-${uid}`)
const messageId = computed(() => `ds-input-msg-${uid}`)

defineExpose({ inputRef })
</script>

<style scoped>
.ds-input-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.ds-input-field__label {
  color: var(--ds-purple-900);
  font-family: var(--ds-font-family-base);
  font-size: var(--ds-subtitle7-size);
  font-weight: var(--ds-subtitle7-weight);
  line-height: 24px;
}

.ds-input-field__box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 13px 16px;
  border: 1px solid var(--ds-purple-main);
  border-radius: 4px;
  background: #ffffff;
  box-sizing: border-box;
}

.ds-input-field__box.is-error {
  border-color: var(--ds-error-main);
  background: var(--ds-error-50);
}

.ds-input-field__icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.ds-input-field__input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--ds-text-primary);
  font-family: var(--ds-font-family-base);
  font-size: 16px;
}

.ds-input-field__input:focus {
  outline: none;
}

.ds-input-field__box.is-error .ds-input-field__input {
  color: var(--ds-error-main);
}

.ds-input-field__message {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  color: var(--ds-error-main);
  font-family: var(--ds-font-family-base);
  font-size: 12px;
}

.ds-input-field__message-icon {
  flex-shrink: 0;
  width: 13px;
  height: 13px;
}
</style>
